// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — the game's own sound cues, and the list of every cue name it will ever play.
//
// WHY THIS FILE EXISTS: `sfx.play()` on an unknown cue is a deliberate silent no-op in the engine
// (`const p = patches[name]; if (!p) return;`), which is the right call for a mixer but means a cue
// NAME TYPO IS PERMANENTLY INVISIBLE. It cannot throw, jsdom cannot hear it, and a screenshot cannot
// show it — a sibling game shipped its signature moment silent for exactly this reason.
//
// So: the custom patches live here, CUES lists every name the game plays, and tests/cues.test.ts
// asserts (a) every name in CUES is known to a real Sfx, and (b) every ping()/sfx.play() literal in
// src/ is in CUES. The second half is what makes the first half load-bearing.

import type { Patch } from '@ben-gy/game-engine/sound';

/**
 * Cues the engine does not ship. The whole set is metal on metal — a torc is a cast neck-ring, and
 * an arcade blip on one would fight everything else about the board.
 */
export const PATCHES: Record<string, Patch> = {
  /** A torc set down on an empty point. A short, bright tick. */
  set: { type: 'triangle', freq: [640, 380], dur: 0.09, gain: 0.19 },
  /** The ride itself — a rising slide as the torc travels. */
  ride: { type: 'sine', freq: [280, 760], dur: 0.13, gain: 0.17 },
  /** Studs turning over under a jump. Played once per stud, pitched up the run. */
  flip: { type: 'square', freq: [820, 1180], dur: 0.05, gain: 0.11 },
  /** A line coming off the board. The game's signature moment, so it gets the long one. */
  lift: { type: 'triangle', freq: [700, 1400], dur: 0.34, gain: 0.27 },
  /** The torc you gave up leaving the field. Falls away rather than rising. */
  give: { type: 'sawtooth', freq: [300, 110], dur: 0.26, gain: 0.22, noise: true },
};

/**
 * EVERY cue name the game plays, engine built-in or custom. Keep it in sync with the source — the
 * test greps the call sites and fails if a literal is missing from this list.
 */
export const CUES: readonly string[] = [
  // custom
  'set',
  'ride',
  'flip',
  'lift',
  'give',
  // engine built-ins the game uses
  'blip',
  'select',
  'win',
  'lose',
  'beat',
  'go',
];
